/**
 * VEXL Frontend Configuration
 * Centralized configuration management for frontend
 */

// =============================================================================
// SECURITY CONFIGURATION
// =============================================================================

// SECURITY: System secrets must NEVER use the VITE_ prefix.
// They are accessed only in server-side API routes via process.env.VEXL_SYSTEM_SECRET.
// DO NOT import any secret here — VITE_ vars are bundled into the client JS.


// =============================================================================
// SUPABASE CONFIGURATION
// =============================================================================

export function getSupabaseConfig() {
  const url = import.meta.env.VITE_SUPABASE_URL;
  const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

  if (!url || !anonKey) {
    throw new Error(
      'VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY environment variables are required. ' +
      'Please set them in your .env file.'
    );
  }

  return { url, anonKey };
}

// =============================================================================
// BACKEND API CONFIGURATION
// =============================================================================

export const BACKEND_URLS = {
  engine: import.meta.env.VITE_ENGINE_URL || 'https://engine.vexl.tech',
  api: import.meta.env.VITE_API_URL || '',
} as const;

// =============================================================================
// FILE UPLOAD CONFIGURATION
// =============================================================================

export const UPLOAD_CONFIG = {
  maxFileSizeBytes: parseInt(import.meta.env.VITE_MAX_UPLOAD_BYTES || '5242880'), // 5MB default
  maxFileSizeMB: parseInt(import.meta.env.VITE_MAX_UPLOAD_MB || '5'),
  allowedExtensions: {
    vector: ['.geojson', '.json', '.shp', '.kml', '.gpkg'],
    raster: ['.tif', '.tiff', '.geotiff'],
  },
} as const;

// =============================================================================
// STORAGE CONFIGURATION
// =============================================================================

export const STORAGE_CONFIG = {
  maxLocalStorageSize: parseInt(import.meta.env.VITE_MAX_LOCAL_STORAGE_BYTES || '5242880'), // 5MB
  maxSessionStorageSize: parseInt(import.meta.env.VITE_MAX_SESSION_STORAGE_BYTES || '2097152'), // 2MB
  keys: {
    conversations: 'vexl-gpt-conversations',
    sessionCache: 'vexl_gpt_active_state',
    novaSpatial: 'nova_spatial_engine_state',
  },
} as const;

// =============================================================================
// CORS CONFIGURATION
// =============================================================================

export const CORS_CONFIG = {
  allowedOrigins: (import.meta.env.VITE_ALLOWED_ORIGINS || 'https://vexl.tech,https://www.vexl.tech')
    .split(',')
    .map(o => o.trim()),
  allowedMethods: ['GET', 'POST', 'OPTIONS'],
  allowedHeaders: ['Content-Type', 'Authorization'],
} as const;

// =============================================================================
// DEVELOPMENT CONFIGURATION
// =============================================================================

export const DEV_CONFIG = {
  authBypass: import.meta.env.DEV && import.meta.env.VITE_DEV_AUTH_BYPASS === 'true',
  isDevelopment: import.meta.env.DEV,
  isProduction: import.meta.env.PROD,
} as const;

// Safety check for DEV_AUTH_BYPASS
if (DEV_CONFIG.authBypass && import.meta.env.PROD) {
  console.error('🚨 SECURITY WARNING: DEV_AUTH_BYPASS is enabled in production!');
}

// =============================================================================
// PAYMENT GATEWAY CONFIGURATION (PayMob)
// =============================================================================

export const PAYMENT_CONFIG = {
  // PayMob Public Config (safe for client-side)
  // SECURITY: apiKey & hmacSecret are server-side only (process.env.PAYMOB_API_KEY)
  paymob: {
    integrationId: import.meta.env.VITE_PAYMOB_INTEGRATION_ID || '',
    iframeId: import.meta.env.VITE_PAYMOB_IFRAME_ID || '',
    baseUrl: import.meta.env.VITE_PAYMOB_BASE_URL || 'https://accept.paymob.com/api',
  },

  
  // Pricing Tiers (EGP)
  pricing: {
    studentPro: {
      monthly: 79,
      name: 'Student Pro',
      id: 'student_pro',
    },
    projectMaster: {
      bundle: 299,
      durationMonths: 4,
      name: 'Project Master',
      id: 'project_master',
    },
  },
  
  // Webhook Configuration
  webhooks: {
    endpoint: '/api/v1/payments/webhook',
    events: ['transaction_success', 'transaction_failed'],
  },
} as const;

// Validation helper
export function validatePaymentConfig(): boolean {
  // Only public client identifiers are permitted in frontend configurations
  const requiredPublicKeys = [
    'VITE_PAYMOB_INTEGRATION_ID',
    'VITE_PAYMOB_IFRAME_ID',
  ];
  
  const missing = requiredPublicKeys.filter(key => !(import.meta as any).env[key]);
  
  if (missing.length > 0 && (import.meta as any).env.PROD) {
    console.warn('⚠️ [Payment Gateway] Optional public payment UI configuration missing:', missing.join(', '));
    return false;
  }
  
  return true;
}

// =============================================================================
// UTILITY FUNCTIONS
// =============================================================================

/**
 * Check if file size is within limits
 */
export function isFileSizeValid(sizeBytes: number): boolean {
  return sizeBytes <= UPLOAD_CONFIG.maxFileSizeBytes;
}

/**
 * Get file extension from filename
 */
export function getFileExtension(filename: string): string {
  return filename.slice(((filename.lastIndexOf('.') - 1) >>> 0) + 2).toLowerCase();
}

/**
 * Check if file type is allowed
 */
export function isFileTypeAllowed(filename: string): boolean {
  const ext = getFileExtension(filename);
  const allowedExtensions: string[] = [
    ...UPLOAD_CONFIG.allowedExtensions.vector,
    ...UPLOAD_CONFIG.allowedExtensions.raster
  ];
  return allowedExtensions.includes(ext);
}

/**
 * Calculate storage size of a value
 */
export function calculateStorageSize(value: unknown): number {
  try {
    return new Blob([JSON.stringify(value)]).size;
  } catch {
    return 0;
  }
}

/**
 * Check if storage has available space
 */
export function hasStorageSpace(value: unknown, maxSize: number): boolean {
  return calculateStorageSize(value) <= maxSize;
}
